import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Upload, Typography, Form, Row, message, DatePicker } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { hasRole, openNotification } from '../../utils';
import ProposalService from '../../services/ProposalService';
import DocumentService from '../../services/DocumentService';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/tr_TR';
import 'dayjs/locale/tr';

const { Text } = Typography;
const { Dragger } = Upload;
const { RangePicker } = DatePicker;
const DEFAULT_START_HOUR = 9;
const DEFAULT_END_HOUR = 18;
const MAX_FILE_SIZE_MB = 3;
const ACCEPTED_FILE_EXTENSIONS = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];

const buildDefaultTime = (hour: number) =>
    dayjs().hour(hour).minute(0).second(0).millisecond(0);

const getFileExtension = (fileName?: string) => {
    const parts = fileName?.split('.') ?? [];
    return parts.length > 1 ? parts.pop()?.toLowerCase() : '';
};

const DocumentUploadModal: React.FC<any> = ({ proposalId, isModalVisible, setIsModalVisible, setIsStatusChange = false, type = 1 }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [fileList, setFileList] = useState<any[]>([]);
    const [documentName, setDocumentName] = useState<string | null>(null);
    const [preSignUrl, setPreSignUrl] = useState<string | null>(null);
    const [isImage, setIsImage] = useState<boolean>(false);
    const [requestedPlanningStartDate, setRequestedPlanningStartDate] = useState<any>(null);
    const [requestedPlanningEndDate, setRequestedPlanningEndDate] = useState<any>(null);

    const canCreatePlanningRequest = hasRole('CUSTOMER') && type === 1;
    const hasSelectedFile = fileList.length > 0;
    const hasPlanningRequest = Boolean(requestedPlanningStartDate && requestedPlanningEndDate);
    const isSaveDisabled = type !== 1 || (!hasSelectedFile && !(canCreatePlanningRequest && hasPlanningRequest));

    const addFileToList = useCallback((newFile: any) => {
        if (!newFile) {
            setFileList([]);
            return;
        }

        const fileExtension = getFileExtension(newFile.name);
        if (!fileExtension || !ACCEPTED_FILE_EXTENSIONS.includes(fileExtension)) {
            setFileList([]);
            message.error('Yalnızca PDF, DOC, DOCX, JPG, JPEG veya PNG dosyaları yüklenebilir.');
            return;
        }

        if (newFile.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
            setFileList([]);
            message.error(`Dosya boyutu en fazla ${MAX_FILE_SIZE_MB} MB olabilir.`);
            return;
        }

        setFileList([newFile]);
    }, []);

    const createUploadFileFromClipboard = useCallback((file: File) => ({
        uid: `pasted-${Date.now()}-${file.name}`,
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'done',
        originFileObj: file,
    }), []);

    useEffect(() => {
        if (isModalVisible && proposalId) {
            fetchProposalDocument();
        }
    }, [isModalVisible, proposalId]);

    useEffect(() => {
        if (!isModalVisible || type !== 1 || isLoading) {
            return;
        }

        const handlePaste = (event: Event) => {
            const clipboardEvent = event as ClipboardEvent;
            const pastedFiles = Array.from(clipboardEvent.clipboardData?.files ?? []);
            if (!pastedFiles.length) {
                return;
            }

            event.preventDefault();
            addFileToList(createUploadFileFromClipboard(pastedFiles[0]));
        };

        window.addEventListener('paste', handlePaste);
        return () => window.removeEventListener('paste', handlePaste);
    }, [addFileToList, createUploadFileFromClipboard, isLoading, isModalVisible, type]);

    const fetchProposalDocument = async () => {
        try {
            const response = await ProposalService.get(proposalId);
            const proposal = response?.data;
            const documentId = proposal?.documentId;

            if (proposal?.requestedPlanningStartDate && proposal?.requestedPlanningEndDate) {
                setRequestedPlanningStartDate(dayjs(proposal.requestedPlanningStartDate));
                setRequestedPlanningEndDate(dayjs(proposal.requestedPlanningEndDate));
            } else {
                setRequestedPlanningStartDate(null);
                setRequestedPlanningEndDate(null);
            }

            if (documentId) {
                const responseDocument: any = await DocumentService.getDocument(documentId);
                const documentData = responseDocument?.data;

                if (documentData) {
                    setDocumentName(documentData.code);
                    setPreSignUrl(documentData.preSignUrl);

                    const fileType = documentData.code.split('.').pop().toLowerCase();
                    setIsImage(['jpg', 'jpeg', 'png'].includes(fileType));
                } else {
                    setDocumentName(null);
                    setPreSignUrl(null);
                    setIsImage(false);
                }
            }
        } catch (error) {
            openNotification('error', 'Hata', 'Belge bilgisi getirilemedi.');
            console.error(error);
        }
    };

    const handleFileChange = ({ fileList }: any) => {
        addFileToList(fileList.slice(-1)[0]);
    };

    const handleUpload = async () => {
        if (isSaveDisabled) {
            message.warning('Lütfen dosya yükleyin veya planlama talep tarihi seçin.');
            return;
        }

        setIsLoading(true);

        try {
            let isDocumentUploaded = false;
            let isPlanningSaved = false;

            if (hasSelectedFile) {
                const fileName = fileList[0].name;
                const uploadResponse: any = await DocumentService.postDocument(fileList[0].originFileObj ?? fileList[0], fileName);
                const documentId: any = uploadResponse?.data?.id;
                if (documentId) {
                    await ProposalService.updateDocumentId(proposalId, { documentId });
                    isDocumentUploaded = true;
                }
            }

            if (canCreatePlanningRequest && hasPlanningRequest) {
                await ProposalService.update(proposalId, {
                    requestedPlanningStartDate: dayjs(requestedPlanningStartDate).toISOString(),
                    requestedPlanningEndDate: dayjs(requestedPlanningEndDate).toISOString(),
                });
                isPlanningSaved = true;
            }

            if (isDocumentUploaded && isPlanningSaved) {
                openNotification('success', 'Başarılı', 'Sözleşme ve planlama talebi kaydedildi.');
                setIsModalVisible(false);

            } else if (isDocumentUploaded) {
                openNotification('success', 'Başarılı', 'Sözleşme Yüklendi');
                setIsModalVisible(false);

            } else if (isPlanningSaved) {
                openNotification('success', 'Başarılı', 'Planlama talebi kaydedildi.');
                setIsModalVisible(false);
            }

            if (isDocumentUploaded) {
                setIsStatusChange(true);
            }
            if (isDocumentUploaded || isPlanningSaved) {
                setFileList([]);
                setIsModalVisible(false);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setFileList([]);
        setDocumentName(null);
        setPreSignUrl(null);
        setIsImage(false);
        setRequestedPlanningStartDate(null);
        setRequestedPlanningEndDate(null);
    };

    const disabledDate = (current: any) => current && current < dayjs().startOf('day');

    const handlePlanningDateChange = (dates: any) => {
        if (dates && dates.length === 2) {
            setRequestedPlanningStartDate(dates[0]);
            setRequestedPlanningEndDate(dates[1]);
            return;
        }
        setRequestedPlanningStartDate(null);
        setRequestedPlanningEndDate(null);
    };

    return (
        <Modal
            title={type === 1 ? <Text className="modal-title">Sözleşme Yükle</Text> : <Text className="modal-title">Sözleşme Görüntüle</Text>}
            visible={isModalVisible}
            onCancel={handleCancel}
            footer={null}
            className="planlama-modal"
        >
            <Form layout="vertical">
                {type === 1 &&
                    <Text>Yüklediğiniz sözleşme, KONTROLX tarafından incelenecek ve uygun ise onaylanacaktır. Lütfen yüklediğiniz sözleşmenin doğru ve eksiksiz olduğundan emin olun.</Text>
                }
                <br />
                {canCreatePlanningRequest && (
                    <Form.Item label="Planlama Talep Edilen Tarih" style={{ marginTop: 16 }}>
                        <RangePicker
                            locale={locale}
                            placeholder={['Başlangıç Tarihi', 'Bitiş Tarihi']}
                            format="DD/MM/YYYY HH:mm"
                            showTime={{
                                format: 'HH:mm',
                                minuteStep: 10,
                                defaultOpenValue: [
                                    buildDefaultTime(DEFAULT_START_HOUR),
                                    buildDefaultTime(DEFAULT_END_HOUR),
                                ],
                            }}
                            value={requestedPlanningStartDate && requestedPlanningEndDate ? [requestedPlanningStartDate, requestedPlanningEndDate] : null}
                            style={{ width: '100%' }}
                            onChange={handlePlanningDateChange}
                            disabledDate={disabledDate}
                        />
                    </Form.Item>
                )}

                {documentName && (
                    <div style={{ marginTop: 16 }}>
                        {/* <Text color='blue' style={{ backgroundColor: '#fafafa' }} strong>Yüklü Dosya: {documentName}</Text> */}
                        {isImage && preSignUrl ? (
                            <div style={{ marginTop: 16, textAlign: 'center' }}>
                                <img src={preSignUrl} alt="Document Preview" style={{ maxWidth: '100%', maxHeight: '200px' }} />
                            </div>
                        ) : null}
                        <Row justify="center" style={{ marginTop: 16 }}>
                            {preSignUrl && (
                                <Button type="link" href={preSignUrl} target="_blank" download>
                                    Dosyayı İndir
                                </Button>
                            )}
                        </Row>
                    </div>
                )}

                {type === 1 && (
                    <Dragger
                        style={{ marginTop: 24 }}
                        fileList={fileList}
                        beforeUpload={() => false}
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        multiple={false}
                        maxCount={1}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Dosyayı buraya sürükleyip bırakın</p>
                        <p className="ant-upload-hint">
                            tıklayarak seçin veya Ctrl+V ile yapıştırın (Maksimum 3 MB)
                        </p>
                    </Dragger>
                )}

                <Row justify={'center'} style={{ marginTop: 32 }}>
                    {type === 1 && (
                        <Button type="primary" loading={isLoading} onClick={handleUpload} className="d-button dark-button width-auto" disabled={isSaveDisabled}>
                            Kaydet
                        </Button>
                    )}
                </Row>
            </Form>
        </Modal>
    );
};

export default DocumentUploadModal;
